import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MiscellaneousServicesOutlinedIcon from '@mui/icons-material/MiscellaneousServicesOutlined';
import { showToastr } from '../../../components/ToastrMessage/Toast.helper';
import seedMockDataIfNeeded from './seedMockData';
import { getFieldConfig } from '../../../constants/serviceFieldConfig';
import './CSCOwnerServices.styles.scss';

const getServices = () => {
  try { return JSON.parse(localStorage.getItem('cscServices') || '[]'); }
  catch { return []; }
};

const fmtDate = (val, comingSoon) => {
  if (comingSoon) return 'Coming Soon';
  return val || '—';
};

const CSCOwnerServices = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState(getServices);
  const [search, setSearch]     = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    seedMockDataIfNeeded();
    setServices(getServices());
  }, []);

  const displayName = (svc) =>
    svc.subCategory && svc.subCategory !== 'Other Not in the List'
      ? svc.subCategory
      : svc.serviceName || '';

  const filtered = services.filter((s) => {
    const q = search.toLowerCase();
    return (
      displayName(s).toLowerCase().includes(q) ||
      s.serviceCategory?.toLowerCase().includes(q) ||
      s.subCategory?.toLowerCase().includes(q)
    );
  });

  const handleDelete = useCallback((id) => {
    const updated = services.filter((s) => s.id !== id);
    localStorage.setItem('cscServices', JSON.stringify(updated));
    setServices(updated);
    setDeleteId(null);
    showToastr('success', 'Service deleted successfully.');
  }, [services]);

  return (
    <div className="cosvc">

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="cosvc__header">
        <div className="cosvc__header-text">
          <h2 className="cosvc__title">My Services</h2>
          <p className="cosvc__subtitle">
            Manage all your registered services
          </p>
        </div>
        <Button
          variant="contained" disableElevation
          startIcon={<AddCircleOutlineIcon />}
          className="cosvc__add-btn"
          onClick={() => navigate('/csc-portal/add-service')}
        >
          Add Service
        </Button>
      </div>

      {/* ── Search + count bar ──────────────────────────────── */}
      <div className="cosvc__toolbar">
        <TextField
          size="small" variant="outlined"
          placeholder="Search by name or category..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="cosvc__search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <span className="cosvc__count">
          {filtered.length} of {services.length} service{services.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Empty state ──────────────────────────────────────── */}
      {services.length === 0 && (
        <div className="cosvc__empty">
          <MiscellaneousServicesOutlinedIcon className="cosvc__empty-icon" />
          <p className="cosvc__empty-title">No services registered yet</p>
          <p className="cosvc__empty-sub">
            Start by adding your first service for customers.
          </p>
          <Button
            variant="contained" disableElevation
            className="cosvc__add-btn"
            onClick={() => navigate('/csc-portal/add-service')}
          >
            Register First Service
          </Button>
        </div>
      )}

      {/* ── No search results ────────────────────────────────── */}
      {services.length > 0 && filtered.length === 0 && (
        <div className="cosvc__empty">
          <SearchIcon className="cosvc__empty-icon" />
          <p className="cosvc__empty-title">No results for "{search}"</p>
          <p className="cosvc__empty-sub">Try a different name or category.</p>
        </div>
      )}

      {/* ── Cards grid ───────────────────────────────────────── */}
      {filtered.length > 0 && (
        <div className="cosvc__grid">
          {filtered.map((svc) => (
            <div key={svc.id} className="cosvc__card">

              {deleteId === svc.id ? (
                /* ── Inline delete confirmation ── */
                <div className="cosvc__del-confirm">
                  <p className="cosvc__del-text">
                    Delete <strong>"{displayName(svc)}"</strong>?<br />
                    <span className="cosvc__del-sub">This cannot be undone.</span>
                  </p>
                  <div className="cosvc__del-actions">
                    <Button
                      size="small" variant="contained" color="error"
                      disableElevation
                      onClick={() => handleDelete(svc.id)}
                    >
                      Yes, Delete
                    </Button>
                    <Button
                      size="small" variant="outlined"
                      onClick={() => setDeleteId(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* ── Card header ── */}
                  <div className="cosvc__card-head">
                    <Chip
                      label={svc.serviceCategory}
                      size="small"
                      className="cosvc__category-chip"
                    />
                    <div className="cosvc__card-actions">
                      <IconButton
                        size="small"
                        className="cosvc__edit-btn"
                        title="Edit service"
                        onClick={() => navigate(`/csc-portal/add-service?edit=${svc.id}`)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="cosvc__del-btn"
                        title="Delete service"
                        onClick={() => setDeleteId(svc.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>

                  {/* ── Sub-category as heading ── */}
                  <h3 className="cosvc__name">{displayName(svc)}</h3>

                  {svc.state && (
                    <div className="cosvc__meta-row">
                      <span className="cosvc__meta-item">
                        <span className="cosvc__meta-label">State:</span> {svc.state}
                      </span>
                    </div>
                  )}

                  {svc.serviceDescription && (
                    <p className="cosvc__desc">{svc.serviceDescription}</p>
                  )}

                  {/* ── Dates ── */}
                  {(() => {
                    const cfg = getFieldConfig(svc.serviceCategory);
                    return (cfg.dates || cfg.examDates) ? (
                      <div className="cosvc__dates">
                        {cfg.dates && (
                          <>
                            <div className="cosvc__date-item">
                              <span className="cosvc__date-label">Start</span>
                              <span className="cosvc__date-val">{svc.startDate || '—'}</span>
                            </div>
                            <div className="cosvc__date-item">
                              <span className="cosvc__date-label">End</span>
                              <span className="cosvc__date-val">{svc.endDate || '—'}</span>
                            </div>
                          </>
                        )}
                        {cfg.examDates && (
                          <>
                            <div className="cosvc__date-item">
                              <span className="cosvc__date-label">Admit Card</span>
                              <span className="cosvc__date-val">
                                {fmtDate(svc.admitCardDate, svc.admitCardComingSoon)}
                              </span>
                            </div>
                            <div className="cosvc__date-item">
                              <span className="cosvc__date-label">Exam</span>
                              <span className="cosvc__date-val">
                                {fmtDate(svc.examDate, svc.examDateComingSoon)}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    ) : null;
                  })()}

                  {/* ── Qualifications ── */}
                  {getFieldConfig(svc.serviceCategory).qualifications && svc.qualifications?.length > 0 && (
                    <div className="cosvc__quals">
                      {svc.qualifications.map((q) => (
                        <Chip key={q} label={q} size="small" className="cosvc__qual-chip" />
                      ))}
                    </div>
                  )}

                  {/* ── Fees ── */}
                  {(svc.feeGeneral || svc.feeEWS || svc.feeOBC || svc.feeScSt) && (
                    <div className="cosvc__fees">
                      {[
                        { label: 'UR',    val: svc.feeGeneral },
                        { label: 'EWS',   val: svc.feeEWS },
                        { label: 'OBC',   val: svc.feeOBC },
                        { label: 'SC/ST', val: svc.feeScSt },
                      ].map(({ label, val }) => val !== '' && val !== undefined && (
                        <span key={label} className="cosvc__fee-item">
                          {label}: <strong>₹{val}</strong>
                        </span>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CSCOwnerServices;
